import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { List } from 'immutable';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  beginsWithVowel,
  isUserVerified,
  flashHighlight,
  // loadExternalScript,
  toSeoUrl,
  get,
} from 'helpers/HelperFunctions';
import { trackEvent, capturePosthogEvent } from 'helpers/Analytics';
import { maybeTrackAudienceTransitionOnSubscriptionSuccess } from 'helpers/audienceTracking';
import config from 'config';

import PaymentModal from 'components/Pitch/PaymentModal';
import AlertBox from 'components/AlertBox/AlertBox';
import HireUser from 'components/Portfolio/HireUser';
import Modal from 'components/Modal/Modal';
import PortfolioHeader from 'components/Portfolio/PortfolioHeader/PortfolioHeader';
import PortfolioLayout from 'components/Portfolio/PortfolioLayout/PortfolioLayout';
import PortfolioPreviewBar from 'components/Portfolio/PortfolioPreviewBar/PortfolioPreviewBar';
import PortfolioSkeleton from 'components/Portfolio/PortfolioSkeleton/PortfolioSkeleton';
import ReportUser from 'components/Portfolio/ReportUser';
import PortfolioTour from 'components/Portfolio/PortfolioTour/PortfolioTour';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import TestimonialsAdd from 'components/Testimonials/TestimonialsAdd';
import ManageTestimonials from 'components/Portfolio/ManageTestimonials/ManageTestimonials';
import manageTestimonialsStyles from 'components/Portfolio/ManageTestimonials/ManageTestimonials.module.scss';
import VerifyUser from 'components/Portfolio/VerifyUser';
import NotFoundContent from 'components/NotFoundContent/NotFoundContent';
import ProfilePictureModal from 'components/Portfolio/ProfilePictureModal/ProfilePictureModal';
import CoverPhotoModal from 'components/Portfolio/CoverPhotoModal/CoverPhotoModal';
import { INVITE_SENT_SUCCESS } from 'constants/successMessages';
import queryString from 'query-string';
import { getRoleCategory } from 'helpers/Constants';
import { computePortfolioQuality } from 'helpers/PortfolioQuality';
import { isTierOneOrTwoCity } from 'helpers/LocationTiers';
import {
  CLIENT_PREVIEW_SEARCH,
  isClientPreview,
  usesTwoColumns,
} from 'helpers/portfolioLayout';
import { showActionToast } from 'components/ActionToast';
import { getCoverSource, setCoverSource } from 'helpers/profileCoverPresets';
import {
  breadcrumbProfilePhoto,
  getThumbnailUrl,
  trackProfilePhotoSave,
  waitForThumbnail,
} from 'helpers/profileMedia';

export default class Portfolio extends PureComponent {
  static propTypes = {
    briefEntities: PropTypes.object,
    creditEntities: PropTypes.object,
    loadedSuggestedRoles: PropTypes.bool,
    loadingSuggestedRoles: PropTypes.bool,
    loadedSuggestedSkills: PropTypes.bool,
    loadingSuggestedSkills: PropTypes.bool,
    loadingPartners: PropTypes.bool,
    loadedPartners: PropTypes.bool,
    location: PropTypes.object,
    match: PropTypes.object,
    partners: PropTypes.array,
    pitches: PropTypes.object,
    portfolio: PropTypes.object,
    portfolioCarousel: PropTypes.array,
    projectEntities: PropTypes.object,
    roleEntities: PropTypes.object,
    sendingUserReport: PropTypes.bool,
    sentUserReports: PropTypes.object,
    suggestedRoles: PropTypes.array,
    suggestedSkills: PropTypes.array,
    testimonialEntities: PropTypes.object,
    userEntities: PropTypes.object,
    voucherEntities: PropTypes.object,
    updatingUser: PropTypes.bool,
    user: PropTypes.object,
    history: PropTypes.object,
    // functions
    acceptPitch: PropTypes.func,
    addToTalentPool: PropTypes.func,
    inviteUser: PropTypes.func,
    isBriefsLoaded: PropTypes.func,
    isCreditsLoaded: PropTypes.func,
    isInTalentPool: PropTypes.func,
    isOwnBriefsLoaded: PropTypes.func,
    isSuggestedRolesLoaded: PropTypes.func,
    isSuggestedSkillsLoaded: PropTypes.func,
    isUserLoaded: PropTypes.func,
    loadBriefs: PropTypes.func,
    loadCredits: PropTypes.func,
    loadPartners: PropTypes.func,
    loadUserTour: PropTypes.func,
    loadOwnBriefs: PropTypes.func,
    loadPendingPitches: PropTypes.func,
    loadSuggestedRoles: PropTypes.func,
    loadSuggestedSkills: PropTypes.func,
    loadTestimonials: PropTypes.func,
    loadUser: PropTypes.func,
    loadUserById: PropTypes.func,
    loadAuth: PropTypes.func,
    reorderCredits: PropTypes.func,
    updateUserTour: PropTypes.func,
    sendUserReport: PropTypes.func,
    setSuccessMessage: PropTypes.func,
    setSelectedRoleCategory: PropTypes.func,
    clearSelectedRoleCategory: PropTypes.func,
    removeFromTalentPool: PropTypes.func,
    verify: PropTypes.func,
    updateUser: PropTypes.func,
    updateLocalUser: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      addTestimonial: false,
      hireRole: null,
      reportComments: '',
      reportReason: 'Inappropriate content uploaded',
      selectedBrief: null,
      selectedBriefVoucher: null,
      selectedPitch: null,
      showAcceptPitch: false,
      showEmailActivateModal: false,
      showHireModal: props.match.params.option === 'hire',
      showHireByDayModal: false,
      showReportModal: props.match.params.option === 'report',
      showTestimonialsModal: props.match.params.option === 'testimonials',
      showVerifyModal: props.match.params.option === 'verify',
      showUpdateAvatar: false,
      showUpdateCover: false,
      verificationUrl: '',
      showMoreAbout: false,
      showPortfolioTour: false,
      testingPartners: props.location.query.testing ?? false,
    };
  }

  componentDidMount() {
    const { username } = this.props.match.params;
    const { location } = this.props;
    const isLoggedIn = this.props.user !== undefined;

    // Redirect to signin if client is not logged in and came from email
    if (!isLoggedIn && location.query.utm_source === 'email') {
      document.location = `/signin?referer=${`/${username}`}`;
    }

    if (queryString.parse(this.props.location.search).success === null) {
      trackEvent('user-subscribed');
    }

    if (__CLIENT__ && this.props.user) {
      void maybeTrackAudienceTransitionOnSubscriptionSuccess({
        user: this.props.user,
        locationSearch: this.props.location?.search || '',
        reloadAuth: this.props.loadAuth,
      });
    }

    trackEvent('user-landed-on-portfolio', {
      portfolioUsername: username,
      loggedIn: isLoggedIn,
      ownPortfolio:
        isLoggedIn && this.props.user.username === username ? true : false,
    });

    // Load user if we haven't already
    if (
      !this.props.portfolio.getIn([username, 'loadedUser'], null) &&
      !this.props.portfolio.getIn([username, 'loadingUser'], null)
    ) {
      this.props.loadUser(username);
    }

    const viewUserId = this.props.portfolio.getIn([username, 'id']);
    const viewUserObj = viewUserId
      ? this.props.userEntities.get(viewUserId.toString(), null).toJS()
      : null;

    if (viewUserObj) {
      this.props.setSelectedRoleCategory(
        getRoleCategory(viewUserObj.top_roles[0])
      );
    }

    this.props.loadPartners('portfolio', this.state.testingPartners);

    if (
      !this.props.portfolio.getIn([username, 'loadedTestimonials'], null) &&
      !this.props.portfolio.getIn([username, 'loadingTestimonials'], null)
    ) {
      this.props.loadTestimonials(username);
    }

    // Load briefs if we haven't already
    if (
      !this.props.portfolio.getIn([username, 'loadedBriefs'], null) &&
      !this.props.portfolio.getIn([username, 'loadingBriefs'], null)
    ) {
      this.props.loadBriefs(username);
    }

    // Load tour data if we haven't already
    if (
      this.props.user &&
      this.props.user.username === username &&
      !this.props.portfolio.getIn([username, 'loadedUserTour'], null) &&
      !this.props.portfolio.getIn([username, 'loadingUserTour'], null)
    ) {
      this.props.loadUserTour(username);
    }

    // Load projects if we haven't already
    if (
      !this.props.portfolio.getIn([username, 'loadedCredits'], null) &&
      !this.props.portfolio.getIn([username, 'loadingCredits'], null)
    ) {
      this.props.loadCredits(username);
    }

    if (
      !this.props.loadedSuggestedRoles &&
      !this.props.loadingSuggestedRoles &&
      !this.props.loadedSuggestedRoles
    ) {
      this.props.loadSuggestedRoles();
    }

    if (
      !this.props.loadedSuggestedSkills &&
      !this.props.loadingSuggestedSkills &&
      !this.props.loadedSuggestedSkills
    ) {
      this.props.loadSuggestedSkills();
    }

    // Load shortlist state if we haven't already
    if (
      this.props.user &&
      this.props.user.username !== username &&
      !this.props.portfolio.hasIn([username, 'isInTalentPool']) &&
      !this.props.portfolio.getIn([username, 'loadingShortlistState'], false)
    ) {
      this.props.isInTalentPool(username);
    }

    // Load own briefs if we haven't already
    if (
      this.props.user &&
      this.props.user.username !== username &&
      !this.props.portfolio.getIn(
        [this.props.user.username, 'loadedOwnBriefs'],
        null
      ) &&
      !this.props.portfolio.getIn(
        [this.props.user.username, 'loadingOwnBriefs'],
        null
      )
    ) {
      this.props.loadOwnBriefs(this.props.user.username);
    }

    // load pending pitches
    if (
      (this.props.user &&
        this.props.user.username !== username &&
        !this.props.pitches) ||
      (this.props.pitches &&
        !this.props.pitches.getIn([username, 'loading']) &&
        !this.props.pitches.getIn([username, 'loaded']))
    ) {
      this.props.loadPendingPitches(username);
    }

    document.addEventListener('scroll', this.handleScroll);

    if (typeof this.props.location.query.highlight !== 'undefined') {
      setTimeout(() => {
        const { query } = this.props.location;
        flashHighlight(query.highlight);

        const queries = query;
        delete queries.highlight;
        this.props.history.replace({
          pathname: this.props.location.pathname,
          query: queries,
        });
      }, 500);
    }

    // Old hack to reload the page if the header renders incorrectly
    // if (__CLIENT__) {
    //   const portfolioGridHeaderCount =
    //     document.getElementsByClassName('portfolioGrid');

    //   if (portfolioGridHeaderCount.length > 1) {
    //     setTimeout(() => {
    //       document.location.reload();
    //     }, 2000);
    //   }
    // }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.sendingUserReport &&
      !this.props.sendingUserReport &&
      prevProps.sentUserReports.length < this.props.sentUserReports.length
    ) {
      this.props.setSuccessMessage('Report submitted');
    }

    // Prompt if not logged in
    if (
      prevProps.match.params.username !== this.props.match.params.username &&
      !this.props.user
    ) {
      if (this._upsellModalTimer) {
        clearTimeout(this._upsellModalTimer);
      }

      this._upsellModalTimer = setTimeout(
        () => this.setState({ showHireModal: true }), // eslint-disable-line react/no-did-update-set-state
        10000
      );
    }

    if (prevProps.updatingUser && !this.props.updatingUser) {
      this.handleEditModeDone();
    }

    if (
      typeof this.props.location.query.highlight !== 'undefined' &&
      prevProps.location.query.highlight !== this.props.location.query.highlight
    ) {
      flashHighlight(this.props.location.query.highlight);

      const queries = this.props.location.query;
      delete queries.highlight;
      this.props.history.replace({
        pathname: this.props.location.pathname,
        query: queries,
      });
    }

    // When user tour data has loaded
    const {
      match: {
        params: { username },
      },
    } = this.props;

    if (this.props.portfolio.getIn([username, 'loadedUserTour'], false)) {
      const prevLoaded = prevProps.portfolio.getIn(
        [username, 'loadedUserTour'],
        false
      );
      const currLoaded = this.props.portfolio.getIn(
        [username, 'loadedUserTour'],
        false
      );

      if (!prevLoaded && currLoaded) {
        this.handleTourDataLoaded();
      }
    }
  }

  componentWillUnmount() {
    if (__CLIENT__) {
      document.removeEventListener('scroll', this.handleScroll);
      window.onbeforeunload = null;
    }

    this.props.clearSelectedRoleCategory();
  }

  static _upsellModalTimer = null;

  handleHireClick = (user, role) => {
    this.setState({
      hireRole: null,
      showHireModal: false,
    });

    window.location.href = `/post-a-job?role=${role}&freelancer=${user.username}`;
  };

  handleReport = (user, reason, comments) => {
    this.props.sendUserReport(user, reason, comments);

    this.setState({
      reportComments: '',
      reportReason: 'Inappropriate content uploaded',
      showReportModal: false,
    });
  };

  handleScroll = () => {
    // Only present once the visitor has expanded the projects grid.
    const creditsGrid = document.getElementById('portfolio-credits-grid');

    if (creditsGrid) {
      const distanceFromBottom =
        creditsGrid.getBoundingClientRect().bottom - window.innerHeight;

      const { username } = this.props.match.params;

      if (
        distanceFromBottom < 250 &&
        this.props.portfolio.getIn([username, 'loadedCredits'], false) &&
        !this.props.portfolio.getIn([username, 'loadingCredits'], false) &&
        this.props.portfolio.getIn([username, 'moreCredits'], false)
      ) {
        this.props.loadCredits(
          username,
          this.props.portfolio.getIn([username, 'creditPagesLoaded'], 0) + 1
        );
      }
    }
  };

  handleToggleShortlist = () => {
    const { username } = this.props.match.params;

    if (!this.props.user) {
      document.location = `/signin?referrer=/${username}`;
    }

    if (this.props.portfolio.getIn([username, 'isInTalentPool'], false)) {
      this.props.removeFromTalentPool(username);
    } else {
      trackEvent('user-shortlists-freelancer', {
        shortlist_type: 'portfolio',
      });
      this.props.addToTalentPool(username);
    }
  };

  handleVerification = (user, url) => {
    this.props.verify(user.username, url);
  };

  handleHireMe = (brief = null, source = 'header') => {
    // Compare Contact clicks before and after the portfolio restyle.
    capturePosthogEvent('portfolio-contact-clicked', {
      source,
      action: brief ? 'invite' : 'contact',
      portfolioUsername: this.props.match.params.username,
      loggedIn: this.props.user !== undefined,
    });

    if (!brief) {
      this.setState({
        showHireModal: true,
      });
    } else {
      trackEvent('user-invites-freelancer', { type: 'portfolio' });
      this.props
        .inviteUser(
          this.props.user,
          this.props.portfolio.getIn([this.props.match.params.username, 'id']),
          brief
        )
        .then(() => {
          this.props.setSuccessMessage(INVITE_SENT_SUCCESS);
        })
        .catch(() => {});
    }
  };

  handleHireByDay = (brief = null) => {
    if (!brief) {
      this.setState({
        showHireByDayModal: true,
      });
    }

    trackEvent('hire-by-day-rate');
  };

  handleAccept = (totalToPay) => {
    const { selectedBrief, selectedPitch } = this.state;

    if (selectedBrief && selectedPitch) {
      this.props.history.push(
        `/manage/briefs/${selectedBrief.id}/pay/${selectedPitch.id}?amount=${
          totalToPay * 100
        }&isHire=true`
      );
    }
  };

  handleEditModeDone = () => {
    if (__CLIENT__) {
      window.onbeforeunload = null;
    }

    this.setState({
      showUpdateCover: false,
      showUpdateAvatar: false,
    });
  };

  handleTourDataLoaded = () => {
    const {
      match: {
        params: { username },
      },
      portfolio,
    } = this.props;

    const portfolioTourCompleted = portfolio.getIn(
      [username, 'tour', 'completed_profile_onboarding'],
      true
    );

    if (!portfolioTourCompleted) {
      this.setState({
        showPortfolioTour: true,
      });
    }
  };

  // In the URL so the site banners can hide too, and leaving the page ends it.
  handlePreviewAsClient = (previewAsClient) => {
    this.props.history.push({
      pathname: this.props.location.pathname,
      search: previewAsClient ? CLIENT_PREVIEW_SEARCH : '',
    });
    window.scrollTo(0, 0);
  };

  handleReorderCredit = (id, position) => {
    const { username } = this.props.match.params;

    trackEvent('freelancer-changes-project-order');
    return this.props.reorderCredits(username, id, position).catch(() =>
      showActionToast({
        message: "We couldn't move that project. Try again.",
      })
    );
  };

  handlePortfolioTourCompleted = () => {
    const { showPortfolioTour } = this.state;
    const {
      match: {
        params: { username },
      },
      updateUserTour,
    } = this.props;

    if (showPortfolioTour) {
      this.setState({
        showPortfolioTour: false,
      });
      const tourData = {
        completed_profile_onboarding: true,
      };
      updateUserTour(username, tourData);
    }
  };

  handleUpdateAvatar = (avatar) => {
    const userId = this.props.portfolio.getIn([this.props.user.username, 'id']);
    const user = userId
      ? this.props.userEntities.get(userId.toString(), null).toJS()
      : this.props.user;

    const previousAvatars = user.avatars;
    const previousUrl = getThumbnailUrl(user.avatars, 'avatars');
    const started = Date.now();
    const bytes = avatar.image && avatar.image.size;
    // Optimistic — portfolio reads entities.users, not auth.user.
    // Do not close the modal until the thumbnail has actually persisted.
    const avatars = {
      ...user.avatars,
      temp: avatar.croppedImage,
      placeholder: false,
    };
    this.props.updateLocalUser(userId, { avatars });

    const updates = {
      avatar: avatar.image,
      crop: {
        avatar: {
          h: avatar.height,
          w: avatar.width,
          x: avatar.x,
          y: avatar.y,
        },
      },
    };

    breadcrumbProfilePhoto('profile photo save start', {
      field: 'avatar',
      userId,
      bytes,
    });

    return Promise.resolve(
      this.props.updateUser(user, updates, false, ['covers'])
    )
      .then(() => {
        breadcrumbProfilePhoto('profile photo PATCH ok', {
          field: 'avatar',
          userId,
        });
        return waitForThumbnail({
          field: 'avatars',
          previousUrl,
          fetchUser: () =>
            Promise.resolve(this.props.loadUserById(userId, ['avatars'])),
        }).then(
          (finalAvatars) => {
            breadcrumbProfilePhoto('profile photo poll persisted', {
              field: 'avatar',
              userId,
            });
            trackProfilePhotoSave({
              outcome: 'persisted',
              field: 'avatar',
              bytes,
              duration_ms: Date.now() - started,
            });
            this.setState({ showUpdateAvatar: false });
            this.props.updateLocalUser(userId, {
              avatars: {
                ...(finalAvatars || avatars),
                temp: avatar.croppedImage,
              },
            });
          },
          (err) => {
            if (!err || err.code !== 'thumbnail_timeout') {
              return Promise.reject(err);
            }
            breadcrumbProfilePhoto('profile photo poll timeout', {
              field: 'avatar',
              userId,
            });
            trackProfilePhotoSave({
              outcome: 'timeout',
              field: 'avatar',
              bytes,
              duration_ms: Date.now() - started,
            });
            this.props.updateLocalUser(userId, { avatars: previousAvatars });
            if (typeof window !== 'undefined') {
              window.alert(
                'We saved your photo but it is still processing. Refresh in a minute — if it does not appear, try again.'
              );
            }
            return Promise.reject(err);
          }
        );
      })
      .catch((err) => {
        if (err && err.code === 'thumbnail_timeout') {
          return Promise.reject(err);
        }
        breadcrumbProfilePhoto('profile photo PATCH error', {
          field: 'avatar',
          userId,
        });
        trackProfilePhotoSave({
          outcome: 'http_error',
          field: 'avatar',
          bytes,
          duration_ms: Date.now() - started,
        });
        this.props.updateLocalUser(userId, { avatars: previousAvatars });
        if (typeof window !== 'undefined') {
          window.alert(
            'We couldn’t update your profile picture. Please try again.'
          );
        }
        return Promise.reject(new Error('avatar update failed'));
      });
  };

  handleRemoveAvatar = () => {
    const userId = this.props.portfolio.getIn([this.props.user.username, 'id']);
    const user = userId
      ? this.props.userEntities.get(userId.toString(), null).toJS()
      : this.props.user;

    this.setState({ showUpdateAvatar: false });

    // Use null (not undefined) so entity merge clears a prior temp preview.
    // Re-apply after the PATCH — the API often returns stale thumbnails because
    // the in-memory user model still has the old files before reload.
    const previousAvatars = user.avatars;
    const clearedAvatars = {
      ...(user.avatars || {}),
      temp: null,
      placeholder: true,
    };
    this.props.updateLocalUser(userId, { avatars: clearedAvatars });

    Promise.resolve(
      this.props.updateUser(user, { remove_avatar: true }, false, ['covers'])
    ).then(
      () => {
        // Defer so this lands after middleware SET_ENTITIES from the PATCH.
        setTimeout(() => {
          this.props.updateLocalUser(userId, { avatars: clearedAvatars });
        }, 0);
      },
      () => {
        setTimeout(() => {
          this.props.updateLocalUser(userId, { avatars: previousAvatars });
        }, 0);
        if (typeof window !== 'undefined') {
          window.alert(
            'We couldn’t remove your profile picture. Please try again.'
          );
        }
      }
    );
  };

  handleOptimisticCoverPreset = (preset) => {
    const userId = this.props.portfolio.getIn([this.props.user.username, 'id']);
    const user = userId
      ? this.props.userEntities.get(userId.toString(), null).toJS()
      : this.props.user;

    // Capture before optimistic mutation so handleUpdateCover can roll back.
    this._previousCoverSnapshot = {
      covers: user.covers,
      source: getCoverSource(userId),
    };

    // Keep modal open until handleUpdateCover PATCH succeeds.
    setCoverSource(userId, { type: 'preset', presetId: preset.id });
    // Force the CSS cover path so the chosen preset paints immediately
    // while the JPEG render + upload job runs.
    this.props.updateLocalUser(userId, {
      covers: {
        ...(user.covers || {}),
        temp: null,
        placeholder: true,
      },
    });
  };

  handleUpdateCover = (cover) => {
    const userId = this.props.portfolio.getIn([this.props.user.username, 'id']);
    const user = userId
      ? this.props.userEntities.get(userId.toString(), null).toJS()
      : this.props.user;

    // Prefer snapshot taken before handleOptimisticCoverPreset; otherwise
    // capture here before this path's own optimistic mutation.
    const previousCover = this._previousCoverSnapshot || {
      covers: user.covers,
      source: getCoverSource(userId),
    };
    this._previousCoverSnapshot = null;

    const field = cover.presetId ? 'cover_preset' : 'cover';
    const previousUrl = getThumbnailUrl(user.covers, 'covers');
    const started = Date.now();
    const bytes = cover.image && cover.image.size;

    if (cover.presetId) {
      setCoverSource(userId, { type: 'preset', presetId: cover.presetId });
    } else {
      setCoverSource(userId, { type: 'upload' });
    }

    const covers = {
      ...(user.covers || {}),
      temp: cover.croppedImage,
      placeholder: false,
    };
    this.props.updateLocalUser(userId, { covers });

    if (!cover.image) {
      trackProfilePhotoSave({
        outcome: 'export_failed',
        field,
        bytes: 0,
        duration_ms: Date.now() - started,
      });
      return Promise.reject(new Error('cover image missing'));
    }

    const updates = {
      cover: cover.image,
      crop: {
        cover: {
          h: cover.height,
          w: cover.width,
          x: cover.x,
          y: cover.y,
        },
      },
    };

    breadcrumbProfilePhoto('profile photo save start', {
      field,
      userId,
      bytes,
    });

    return Promise.resolve(
      this.props.updateUser(user, updates, false, ['covers'])
    )
      .then(() => {
        breadcrumbProfilePhoto('profile photo PATCH ok', {
          field,
          userId,
        });
        return waitForThumbnail({
          field: 'covers',
          previousUrl,
          fetchUser: () =>
            Promise.resolve(this.props.loadUserById(userId, ['covers'])),
        }).then(
          (finalCovers) => {
            breadcrumbProfilePhoto('profile photo poll persisted', {
              field,
              userId,
            });
            trackProfilePhotoSave({
              outcome: 'persisted',
              field,
              bytes,
              duration_ms: Date.now() - started,
            });
            this.setState({ showUpdateCover: false });
            this.props.updateLocalUser(userId, {
              covers: {
                ...(finalCovers || covers),
                temp: cover.croppedImage,
              },
            });
          },
          (err) => {
            if (!err || err.code !== 'thumbnail_timeout') {
              return Promise.reject(err);
            }
            breadcrumbProfilePhoto('profile photo poll timeout', {
              field,
              userId,
            });
            trackProfilePhotoSave({
              outcome: 'timeout',
              field,
              bytes,
              duration_ms: Date.now() - started,
            });
            setCoverSource(userId, previousCover.source);
            this.props.updateLocalUser(userId, {
              covers: previousCover.covers,
            });
            if (typeof window !== 'undefined') {
              window.alert(
                'We saved your cover but it is still processing. Refresh in a minute — if it does not appear, try again.'
              );
            }
            return Promise.reject(err);
          }
        );
      })
      .catch((err) => {
        if (err && err.code === 'thumbnail_timeout') {
          return Promise.reject(err);
        }
        breadcrumbProfilePhoto('profile photo PATCH error', {
          field,
          userId,
        });
        trackProfilePhotoSave({
          outcome: 'http_error',
          field,
          bytes,
          duration_ms: Date.now() - started,
        });
        setCoverSource(userId, previousCover.source);
        this.props.updateLocalUser(userId, { covers: previousCover.covers });
        if (typeof window !== 'undefined') {
          window.alert(
            'We couldn’t update your cover photo. Please try again.'
          );
        }
        return Promise.reject(new Error('cover update failed'));
      });
  };

  handleRemoveCover = () => {
    const userId = this.props.portfolio.getIn([this.props.user.username, 'id']);
    const user = userId
      ? this.props.userEntities.get(userId.toString(), null).toJS()
      : this.props.user;

    this.setState({ showUpdateCover: false });

    const previousCovers = user.covers;
    const previousSource = getCoverSource(userId);

    setCoverSource(userId, { type: 'auto' });
    const clearedCovers = {
      ...(user.covers || {}),
      temp: null,
      placeholder: true,
    };
    this.props.updateLocalUser(userId, { covers: clearedCovers });

    Promise.resolve(
      this.props.updateUser(user, { remove_cover: true }, false, ['covers'])
    ).then(
      () => {
        setTimeout(() => {
          this.props.updateLocalUser(userId, { covers: clearedCovers });
        }, 0);
      },
      () => {
        setTimeout(() => {
          setCoverSource(userId, previousSource);
          this.props.updateLocalUser(userId, { covers: previousCovers });
        }, 0);
        if (typeof window !== 'undefined') {
          window.alert(
            'We couldn’t remove your cover photo. Please try again.'
          );
        }
      }
    );
  };

  showAcceptPitchModal = (pitch) => {
    const brief = pitch.notice;

    if (typeof pitch.sender !== 'object') {
      pitch.sender = this.props.userEntities[pitch.sender];
    }

    if (typeof brief.voucher !== 'object') {
      brief.voucher = this.props.voucherEntities[brief.voucher];
    }

    this.setState({
      showAcceptPitch: true,
      selectedBrief: brief,
      selectedBriefVoucher: brief.voucher,
      selectedPitch: pitch,
    });
  };

  openHireModal = (showHireModal) => {
    if (showHireModal === true) {
      trackEvent('user-opens-contact-modal');
    }
    return showHireModal;
  };

  render() {
    const {
      user,
      briefEntities,
      creditEntities,
      match: {
        params: { username },
      },
      pitches,
      portfolio,
      portfolioCarousel,
      projectEntities,
      roleEntities,
      sentUserReports,
      suggestedRoles,
      suggestedSkills,
      testimonialEntities,
      userEntities,
      partners,
      loadedPartners,
    } = this.props;

    const {
      hireRole,
      reportComments,
      reportReason,
      showEmailActivateModal,
      showHireModal,
      showHireByDayModal,
      showReportModal,
      showTestimonialsModal,
      showVerifyModal,
      showUpdateAvatar,
      showUpdateCover,
      verificationUrl,
      showMoreAbout,
      showPortfolioTour,
    } = this.state;

    const viewUserId = portfolio.getIn([username, 'id']);
    const viewUserObj = viewUserId
      ? userEntities.get(viewUserId.toString(), null).toJS()
      : null;

    // Only render if viewUserObj is loaded
    if (!viewUserObj) {
      return portfolio.getIn([username, 'loadedUser'], false) ? null : (
        <PortfolioSkeleton />
      );
    }

    const isOwner = !!(user && user.id === viewUserObj.id);
    // Previewing as a client renders the public view of your own portfolio.
    const isPreviewing = isOwner && isClientPreview(this.props.location.search);
    const isOwn = isOwner && !isPreviewing;
    const noop = () => {};
    // Projects the portfolio lists; undefined until the first credits page.
    const projectCount = portfolio.getIn([username, 'creditsTotal']);
    const twoColumns = usesTwoColumns(viewUserObj, projectCount);
    const testimonialTotal = portfolio.getIn(
      [username, 'testimonialTotal'],
      viewUserObj.stats?.num_of_testimonials || 0
    );
    const locationFindUrl =
      viewUserObj.top_roles.length > 0
        ? '/find/' +
          toSeoUrl(viewUserObj.top_roles[0]) +
          's' +
          (viewUserObj.location.country_code !== ''
            ? '/' + viewUserObj.location.country_code.replace('gb', 'uk')
            : '') +
          (viewUserObj.location.country_code !== '' &&
          viewUserObj.location.city_slug !== '' &&
          isTierOneOrTwoCity(
            viewUserObj.location.country_code,
            viewUserObj.location.city_slug
          )
            ? '/' + viewUserObj.location.city_slug
            : '')
        : null;

    // Get loading state for own briefs only
    const isLoadingOwnBriefs =
      user && portfolio.getIn([user.username, 'loadingOwnBriefs'], false);

    const isReorderingCredits = portfolio.getIn([
      username,
      'isReorderingCredits',
    ]);

    const portfolioCarouselIndex =
      viewUserObj && portfolioCarousel
        ? portfolioCarousel.indexOf(viewUserObj.id)
        : -1;

    const title = `${
      viewUserObj ? viewUserObj.displayname : username
    }'s portfolio on Twine`;
    const metaDescription = viewUserObj
      ? `${viewUserObj.displayname} is${
          (viewUserObj.top_roles.length > 0
            ? (beginsWithVowel(viewUserObj.top_roles[0]) ? ' an ' : ' a ') +
              viewUserObj.top_roles[0]
            : '') +
          (viewUserObj.location.text !== ''
            ? ' from ' + viewUserObj.location.text
            : '')
        } on Twine. View their freelance projects and hire them to work on your job.`
      : '';

    const portfolioQuality = computePortfolioQuality(viewUserObj);
    const shouldIndex = portfolioQuality.shouldIndex;

    if (
      !isNaN(username) ||
      (portfolio.getIn([username, 'loadedUser'], null) &&
        (!viewUserObj || !viewUserObj.active))
    ) {
      return <NotFoundContent />;
    }

    return (
      <div>
        <Helmet
          title={title}
          link={[{ rel: 'canonical', href: `${config.baseUrl}/${username}` }]}
          meta={[
            ...(!shouldIndex ? [{ name: 'robots', content: 'noindex' }] : []),
            { property: 'og:title', content: title },
            { name: 'twitter:title', content: title },
            { name: 'description', content: metaDescription },
            { property: 'og:description', content: metaDescription },
            { name: 'twitter:description', content: metaDescription },
            { property: 'og:url', content: `${config.baseUrl}/${username}` },
            {
              property: 'og:image',
              content:
                viewUserObj &&
                typeof viewUserObj.avatars.images['190'] !== 'undefined'
                  ? viewUserObj.avatars.images['190'].square.main
                  : '',
            },
            {
              name: 'twitter:image',
              content:
                viewUserObj &&
                typeof viewUserObj.avatars.images['190'] !== 'undefined'
                  ? viewUserObj.avatars.images['190'].square.main
                  : '',
            },
          ]}
        />
        {get(viewUserObj, ['rating', 'count'], 0) > 0 && (
          <div>
            <meta content={viewUserObj.displayname} />
            <meta content={viewUserObj.displayname} />
            <div>
              <span content={viewUserObj.rating.count} />
              <span
                content={
                  viewUserObj.rating.avg < 4 ? 4.1 : viewUserObj.rating.avg
                }
              />
            </div>
          </div>
        )}
        {isPreviewing && (
          <PortfolioPreviewBar
            onExit={() => this.handlePreviewAsClient(false)}
          />
        )}
        <PortfolioHeader
          existingBriefs={
            user
              ? portfolio
                  .getIn([user.username, 'ownBriefs'], List([]))
                  .map((briefId) => briefEntities.get(briefId.toString()))
                  .toJS()
              : []
          }
          loggedInUser={user}
          onUpdateAvatar={() => this.setState({ showUpdateAvatar: true })}
          onUpdateCover={() => this.setState({ showUpdateCover: true })}
          nextUser={
            portfolioCarouselIndex >= 0 &&
            portfolioCarouselIndex < portfolioCarousel.length - 1
              ? userEntities
                  .get(portfolioCarousel[portfolioCarouselIndex + 1].toString())
                  .toJS()
              : null
          }
          onAccept={this.showAcceptPitchModal}
          onHire={(brief) => this.handleHireMe(brief, 'header')}
          onHireByDay={(brief) => this.handleHireByDay(brief)}
          onRatings={() => this.setState({ showTestimonialsModal: true })}
          onVerify={() => this.setState({ showVerifyModal: true })}
          verificationState={isUserVerified(viewUserObj)}
          pitches={
            pitches &&
            username &&
            pitches.getIn([username, 'pitches'], List()).toJS()
          }
          prevUser={
            portfolioCarouselIndex > 0
              ? userEntities
                  .get(portfolioCarousel[portfolioCarouselIndex - 1].toString())
                  .toJS()
              : null
          }
          user={viewUserObj}
          shortlistState={portfolio.getIn([username, 'isInTalentPool'], false)}
          toggleShortlist={this.handleToggleShortlist}
          isLoadingOwnBriefs={isLoadingOwnBriefs}
          locationFindUrl={locationFindUrl}
          narrow={!twoColumns}
          projectCount={projectCount}
          previewAsClient={isPreviewing}
          onPreviewAsClient={() => this.handlePreviewAsClient(true)}
        />
        <PortfolioLayout
          twoColumns={twoColumns}
          user={viewUserObj}
          loggedInUser={user}
          isOwn={isOwn}
          isPreviewing={isPreviewing}
          credits={portfolio
            .getIn([username, 'credits'], List([]))
            .map((creditId) => {
              let credit = creditEntities.get(creditId.toString());
              credit = credit.set(
                'project',
                projectEntities.get(credit.get('project_id').toString())
              );
              credit = credit.setIn(
                ['roles'],
                credit
                  .getIn(['roles'], [])
                  .map((roleId) => roleEntities.get(roleId.toString()))
              );
              return credit;
            })
            .toJS()}
          creditsTotal={projectCount || 0}
          projectCount={projectCount}
          isReorderingCredits={isReorderingCredits}
          loadingCredits={portfolio.getIn([username, 'loadingCredits'], false)}
          moreCredits={portfolio.getIn([username, 'moreCredits'], false)}
          onLoadMoreCredits={() =>
            this.props.loadCredits(
              username,
              portfolio.getIn([username, 'creditPagesLoaded'], 0) + 1
            )
          }
          onReorderCredit={this.handleReorderCredit}
          testimonials={portfolio
            .getIn([username, 'testimonials'], List([]))
            .map((testimonialId) =>
              testimonialEntities.get(testimonialId.toString())
            )
            .toJS()}
          testimonialTotal={testimonialTotal}
          onTestimonials={() => this.setState({ showTestimonialsModal: true })}
          onEmailActivate={() =>
            this.setState({ showEmailActivateModal: true })
          }
          onReport={
            isPreviewing ? noop : () => this.setState({ showReportModal: true })
          }
          sentReport={sentUserReports.includes(viewUserObj.id)}
          showMoreAbout={showMoreAbout}
          onShowMoreAbout={() =>
            this.setState({ showMoreAbout: !showMoreAbout })
          }
          partners={partners}
          loadedPartners={loadedPartners}
          onContact={
            isPreviewing ? noop : () => this.handleHireMe(null, 'hire-band')
          }
        />
        <Modal
          show={this.openHireModal(showHireModal)}
          onExit={() => this.setState({ showHireModal: false })}
          modalStyle={{ padding: 0 }}
        >
          <HireUser
            onHide={() => this.setState({ showHireModal: false })}
            user={viewUserObj}
            selectedRole={hireRole}
            suggestedRoles={suggestedRoles}
            suggestedSkills={suggestedSkills}
            onRoleSelect={(role) => this.setState({ hireRole: role })}
            onNext={(hireUser, role) => this.handleHireClick(hireUser, role)}
          />
        </Modal>
        <AlertBox
          show={showHireByDayModal}
          onConfirm={() => this.setState({ showHireByDayModal: false })}
          title="Pay Freelancer"
          text={
            <p style={{ textAlign: 'left' }}>
              {`If you're already working with the freelancer, go to `}
              <Link to="/ats">Manage Jobs</Link>
              {` and then select the
              job you've hired them for.`}
              <br />
              <br />
              {`If you've not worked with the freelancer before, you need to `}
              <a href="/post-a-job">post a job with your requirements</a>.
              After, come back to this portfolio page and click{' '}
              <strong>invite</strong>.
            </p>
          }
          icon="info"
          theme="info"
        />
        <Modal
          show={showReportModal}
          onExit={() => this.setState({ showReportModal: false })}
        >
          <ReportUser
            onChangeReportComments={(comments) =>
              this.setState({ reportComments: comments })
            }
            onChangeReportReason={(reason) =>
              this.setState({ reportReason: reason })
            }
            onReport={(reportUser, reason, comments) =>
              this.handleReport(reportUser, reason, comments)
            }
            reportComments={reportComments}
            reportReason={reportReason}
            user={viewUserObj}
          />
        </Modal>
        <Modal
          show={showEmailActivateModal}
          onExit={() => this.setState({ showEmailActivateModal: false })}
          closeButton
        >
          <div className="p++">
            <p className="text-center">
              <SvgIcon icon="email" color="#0777DA" size="48" />
            </p>
            <p
              style={{
                textAlign: 'center',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: '700',
                fontSize: '20px',
              }}
            >
              <strong>Confirm your email</strong>
            </p>
            <p
              style={{
                textAlign: 'center',
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '16px',
                maxWidth: '350px',
                margin: 'auto',
              }}
            >
              Twine has sent you an activation email.
              <br />
              Open your email and <b>click the link</b> to activate your account
              before you can edit your profile.
              <br />
            </p>
            <p
              style={{
                textAlign: 'center',
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '14px',
                color: '#666',
                marginTop: '20px',
              }}
            >
              Once activated,{' '}
              <a href="#" onClick={() => window.location.reload()}>
                reload the page
              </a>
              .
            </p>
          </div>
        </Modal>
        <Modal
          onEnter={() =>
            !portfolio.getIn([username, 'loadedTestimonials'], false)
              ? this.props.loadTestimonials(username)
              : null
          }
          onExit={() =>
            this.setState({
              showTestimonialsModal: false,
              addTestimonial: false,
            })
          }
          show={showTestimonialsModal}
          closeButton
          modalClassName={manageTestimonialsStyles.modal}
        >
          {!this.state.addTestimonial ? (
            <ManageTestimonials
              user={viewUserObj}
              isOwn={isOwn}
              total={portfolio.getIn([username, 'testimonialTotal'], 0)}
              loading={portfolio.getIn(
                [username, 'loadingTestimonials'],
                false
              )}
              more={portfolio.getIn([username, 'moreTestimonials'], false)}
              onLoadMore={() =>
                this.props.loadTestimonials(
                  username,
                  portfolio.getIn([username, 'testimonialPagesLoaded'], 0) + 1
                )
              }
              onAsk={() => this.setState({ addTestimonial: true })}
              onClose={() =>
                this.setState({
                  showTestimonialsModal: false,
                  addTestimonial: false,
                })
              }
              testimonials={portfolio
                .getIn([username, 'testimonials'], List([]))
                .map((testimonialId) =>
                  testimonialEntities.get(testimonialId.toString())
                )
                .toJS()}
            />
          ) : (
            <TestimonialsAdd
              onBack={() => this.setState({ addTestimonial: false })}
            />
          )}
        </Modal>
        <Modal
          show={showVerifyModal}
          onExit={() => this.setState({ showVerifyModal: false })}
        >
          <VerifyUser
            user={viewUserObj}
            currentStep={
              portfolio.getIn(
                [username, 'isVerified'],
                user ? user.verified : false
              )
                ? 2
                : 1
            }
            verificationUrl={verificationUrl}
            onValueChanged={(url) => this.setState({ verificationUrl: url })}
            onClickedVerify={(verifyUser, url) =>
              this.handleVerification(verifyUser, url)
            }
          />
        </Modal>
        {this.state.showAcceptPitch && (
          <Modal
            show={this.state.showAcceptPitch}
            onExit={() => this.setState({ showAcceptPitch: false })}
          >
            <PaymentModal
              brief={this.state.selectedBrief}
              pitch={this.state.selectedPitch}
              onAccept={(totalToPay) => this.handleAccept(totalToPay)}
              hireOthers={() => this.setState({ showAcceptPitch: false })}
            />
          </Modal>
        )}
        <ProfilePictureModal
          show={showUpdateAvatar}
          avatars={viewUserObj && viewUserObj.avatars}
          displayName={viewUserObj && viewUserObj.displayname}
          onCancel={() => this.setState({ showUpdateAvatar: false })}
          onSave={this.handleUpdateAvatar}
          onRemove={this.handleRemoveAvatar}
        />
        <CoverPhotoModal
          show={showUpdateCover}
          user={viewUserObj}
          onCancel={() => this.setState({ showUpdateCover: false })}
          onSave={this.handleUpdateCover}
          onOptimisticPreset={this.handleOptimisticCoverPreset}
          onRemove={this.handleRemoveCover}
        />
        {user && viewUserObj && user.id === viewUserObj.id && (
          <Modal backgroundExit={false} show={showPortfolioTour}>
            <PortfolioTour
              user={viewUserObj}
              loggedInUser={user}
              onExit={this.handlePortfolioTourCompleted}
            />
          </Modal>
        )}
      </div>
    );
  }
}
